import React, { useState } from "react";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  FileText,
  ChevronDown,
  Headphones,
} from "lucide-react";
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  LinearProgress,
} from "@mui/material";
import { VoiceConsentDialog } from "./VoiceConsentDialog";
import { useScrollytellTranslation } from "../Admin/scrollytellContext";

const formatTime = (seconds) => {
  if (isNaN(seconds) || seconds < 0) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m < 10 ? "0" : ""}${m}:${s < 10 ? "0" : ""}${s}`;
};

export const AudioPlayerBar = ({ state = {}, handlers = {} }) => {
  const t = useScrollytellTranslation();
  const [isMinimized, setIsMinimized] = useState(false);
  const {
    isPlaying = false,
    isAutoplayActive = false,
    speed = "normal",
    trackTitle = "",
    activeSpokenText = "",
    currentTime = 0,
    duration = 0,
    isMuted = false,
    isTranscriptOpen = false,
    transcript = "",
    status = "IDLE",
    downloadProgress = null,
    voicePreference = null,
    isConsentDialogOpen = false,
  } = state;

  const {
    onTogglePlay = () => {},
    onChangeSpeed = () => {},
    onSeek = () => {},
    onToggleMute = () => {},
    onToggleTranscript = () => {},
    onResumeAutoplay = () => {},
    onSwitchToSystemVoice = () => {},
    onToggleVoiceMode = () => {},
    onAcceptNeuralVoice = () => {},
    onUseSystemVoice = () => {},
    onCloseConsentDialog = () => {},
  } = handlers;

  const handleSeekChange = (e) => {
    onSeek(Number(e.target.value));
  };

  const handleSpeedChange = (e) => {
    onChangeSpeed(e.target.value);
  };

  const isAudioActive = isPlaying || isAutoplayActive;

  return (
    <>
      {isMinimized ? (
        <Box
          sx={{
            position: "fixed",
            bottom: 16,
            left: 16,
            zIndex: 1000,
          }}
        >
          <IconButton
            onClick={() => setIsMinimized(false)}
            aria-label={t("audioPlayerBar.showBarAriaLabel", "Vis lydguide")}
            title={t("audioPlayerBar.showBarAriaLabel", "Vis lydguide")}
            sx={{
              width: 44,
              height: 44,
              minWidth: 44,
              minHeight: 44,
              backgroundColor: "rgba(18, 18, 18, 0.55)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              color: "#FFFFFF",
              borderRadius: "999px",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
              "&:hover": {
                backgroundColor: "rgba(18, 18, 18, 0.8)",
              },
            }}
          >
            <Headphones size={22} />
          </IconButton>
        </Box>
      ) : (
        <Box
          component="aside"
          aria-label={t("audioPlayerBar.ariaLabel", "Lydguide og avspillingskontroller")}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: "rgba(18, 18, 18, 0.55)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            borderTop: "1px solid rgba(255, 255, 255, 0.15)",
            color: "#FFFFFF",
            boxShadow: "0 -2px 12px rgba(0, 0, 0, 0.2)",
            padding: "8px 20px",
            display: "flex",
            flexWrap: "nowrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            fontFamily: "Inter, sans-serif",
            boxSizing: "border-box",
            "@media (max-width: 767px)": {
              flexWrap: "wrap",
              padding: "8px 12px",
              gap: "8px",
            },
          }}
        >
        {/* Left Section: Single Play/Pause Button + Track Title + Subtitle Excerpt */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flex: "0 1 340px",
            minWidth: 0,
            overflow: "hidden",
            "@media (max-width: 767px)": {
              flex: "1 1 45%",
              gap: "8px",
            },
          }}
        >
          <IconButton
            onClick={onTogglePlay}
            sx={{
              color: "#FFFFFF",
              backgroundColor: "#4484F1",
              "&:hover": { backgroundColor: "#3367D6" },
              width: 42,
              height: 42,
              flexShrink: 0,
              "@media (max-width: 767px)": {
                width: 34,
                height: 34,
              },
            }}
            aria-label={
              isAudioActive
                ? t("audioPlayerBar.pause", "Pause avspilling")
                : t("audioPlayerBar.play", "Spill av historien")
            }
          >
            {isAudioActive ? <Pause size={20} /> : <Play size={20} />}
          </IconButton>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              flex: 1,
              overflow: "hidden",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#FFFFFF",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {trackTitle || t("audioPlayerBar.defaultTrackTitle", "Lydguide")}
            </Typography>
            {activeSpokenText && (
              <Typography
                variant="caption"
                title={activeSpokenText}
                sx={{
                  color: "#90CAF9",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                  width: "100%",
                }}
              >
                «{activeSpokenText}»
              </Typography>
            )}
            <Typography
              variant="caption"
              sx={{ color: "#AAAAAA", whiteSpace: "nowrap" }}
            >
              {formatTime(currentTime)} / {formatTime(duration)}
            </Typography>
          </Box>
        </Box>

        {/* Center Section: Progress / Seek Slider or Download Progress */}
        {status === "DOWNLOADING_MODEL" ? (
          <Box
            role="status"
            aria-live="polite"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              flex: "1 1 280px",
              minWidth: 160,
              maxWidth: 540,
              mx: "16px",
              "@media (max-width: 767px)": {
                order: 3,
                flex: "1 1 100%",
                maxWidth: "100%",
                mx: 0,
                gap: "8px",
                flexWrap: "wrap",
              },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "#90CAF9",
                  display: "block",
                  mb: 0.5,
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {downloadProgress?.label ||
                  t(
                    "audioPlayerBar.downloadProgressDefault",
                    "Laster ned norsk stemmemodell: 0 % (0.0 / 60.0 MB)"
                  )}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={downloadProgress?.percent || 0}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "#333333",
                  "& .MuiLinearProgress-bar": { backgroundColor: "#4484F1" },
                }}
              />
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={onSwitchToSystemVoice}
              aria-label={t(
                "audioPlayerBar.cancelDownloadAriaLabel",
                "Avbryt nedlasting og bytt til systemstemme"
              )}
              sx={{
                color: "#FFFFFF",
                borderColor: "#666666",
                textTransform: "none",
                fontSize: "12px",
                whiteSpace: "nowrap",
                px: 1.5,
                py: 0.5,
                minHeight: "40px",
                "&:hover": { borderColor: "#AAAAAA" },
                "@media (max-width: 767px)": {
                  fontSize: "11px",
                  px: 1,
                  minHeight: "34px",
                },
              }}
            >
              {t("audioPlayerBar.switchToSystemVoice", "Bytt til systemstemme")}
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flex: "1 1 200px",
              minWidth: 120,
              maxWidth: 480,
              mx: "16px",
              "@media (max-width: 767px)": {
                order: 3,
                flex: "1 1 100%",
                maxWidth: "100%",
                mx: 0,
              },
            }}
          >
            <input
              type="range"
              aria-label={t("audioPlayerBar.seekAriaLabel", "Søk i lydspor")}
              aria-valuemin={0}
              aria-valuemax={duration || 100}
              aria-valuenow={currentTime}
              value={currentTime}
              min={0}
              max={duration || 100}
              onChange={handleSeekChange}
              style={{
                width: "100%",
                height: "6px",
                accentColor: "#4484F1",
                cursor: "pointer",
              }}
            />
          </Box>
        )}

        {/* Right Section: Speed, Mute, Transcript, Voice Mode */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexShrink: 0,
            "@media (max-width: 767px)": {
              gap: "4px",
              flexWrap: "wrap",
              justifyContent: "flex-end",
            },
          }}
        >
          {voicePreference && (
            <Button
              size="small"
              variant="outlined"
              onClick={onToggleVoiceMode}
              aria-label={
                voicePreference === "neural"
                  ? t("audioPlayerBar.aiVoiceAria", "AI-stemme (Norsk)")
                  : t("audioPlayerBar.systemVoiceAria", "Systemstemme")
              }
              sx={{
                color: "#FFFFFF",
                borderColor:
                  voicePreference === "neural" ? "#4484F1" : "#555555",
                backgroundColor:
                  voicePreference === "neural"
                    ? "rgba(68, 132, 241, 0.2)"
                    : "transparent",
                fontSize: "12px",
                textTransform: "none",
                px: 1,
                py: 0.5,
                minHeight: "40px",
                whiteSpace: "nowrap",
                "&:hover": { borderColor: "#4484F1" },
                "@media (max-width: 767px)": {
                  fontSize: "11px",
                  px: 0.75,
                  minHeight: "34px",
                },
              }}
            >
              {voicePreference === "neural"
                ? t("audioPlayerBar.aiVoiceLabel", "AI-stemme")
                : t("audioPlayerBar.systemVoiceLabel", "Systemstemme")}
            </Button>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              component="label"
              htmlFor="audio-speed-select"
              sx={{
                display: "inline",
                marginRight: "6px",
                fontSize: "12px",
                color: "#AAAAAA",
                whiteSpace: "nowrap",
                "@media (max-width: 767px)": {
                  display: "none",
                },
              }}
            >
              {t("audioPlayerBar.speedLabel", "Hastighet")}
            </Box>
            <select
              id="audio-speed-select"
              aria-label={t("audioPlayerBar.speedAriaLabel", "Hastighet for avspilling")}
              value={speed}
              onChange={handleSpeedChange}
              style={{
                backgroundColor: "#2C2C2C",
                color: "#FFFFFF",
                border: "1px solid #555555",
                borderRadius: "4px",
                padding: "4px 8px",
                fontSize: "12px",
                cursor: "pointer",
              }}
            >
              <option value="slow">0.75x</option>
              <option value="normal">1.0x</option>
              <option value="fast">1.5x</option>
            </select>
          </Box>

          <IconButton
            onClick={onToggleMute}
            sx={{
              color: "#FFFFFF",
              width: 40,
              height: 40,
              "@media (max-width: 767px)": {
                width: 34,
                height: 34,
              },
            }}
            aria-label={
              isMuted
                ? t("audioPlayerBar.unmute", "Slå på lyd")
                : t("audioPlayerBar.mute", "Demp lyd")
            }
          >
            {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </IconButton>

          <IconButton
            onClick={onToggleTranscript}
            sx={{
              color: isTranscriptOpen ? "#4484F1" : "#FFFFFF",
              width: 40,
              height: 40,
              "@media (max-width: 767px)": {
                width: 34,
                height: 34,
              },
            }}
            aria-label={t("audioPlayerBar.transcriptAriaLabel", "Transkripsjon")}
            aria-expanded={isTranscriptOpen}
          >
            <FileText size={18} />
          </IconButton>

          <IconButton
            onClick={() => setIsMinimized(true)}
            sx={{
              color: "#FFFFFF",
              width: 40,
              height: 40,
              "@media (max-width: 767px)": {
                width: 34,
                height: 34,
              },
            }}
            aria-label={t("audioPlayerBar.hideBarAriaLabel", "Skjul avspillingslinje")}
            title={t("audioPlayerBar.hideBarAriaLabel", "Skjul avspillingslinje")}
          >
            <ChevronDown size={18} />
          </IconButton>
        </Box>
      </Box>
      )}

      {/* Floating resume pill when paused by user */}
      {status === "PAUSED_BY_USER" && (
        <Box
          sx={{
            position: "fixed",
            bottom: 80,
            right: 20,
            zIndex: 1100,
          }}
        >
          <Button
            variant="contained"
            onClick={onResumeAutoplay}
            sx={{
              backgroundColor: "#4484F1",
              color: "#FFFFFF",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "24px",
              padding: "10px 20px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
              "&:hover": { backgroundColor: "#3367D6" },
            }}
          >
            {t("audioPlayerBar.resumeAutoplay", "Fortsett automatisk avspilling ▶")}
          </Button>
        </Box>
      )}

      {/* Transcript Dialog */}
      <Dialog
        open={isTranscriptOpen}
        onClose={onToggleTranscript}
        aria-labelledby="audio-transcript-title"
      >
        <DialogTitle id="audio-transcript-title">
          {t("audioPlayerBar.transcriptTitle", `Transkripsjon: ${trackTitle || "Lydspor"}`)}
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ color: "text.primary", whiteSpace: "pre-wrap" }}>
            {transcript ||
              t(
                "audioPlayerBar.noTranscript",
                "Ingen transkripsjon tilgjengelig for dette sporet."
              )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onToggleTranscript}>{t("audioPlayerBar.close", "Lukk")}</Button>
        </DialogActions>
      </Dialog>

      {/* Piper Neural TTS Consent Dialog */}
      <VoiceConsentDialog
        open={Boolean(isConsentDialogOpen)}
        onAcceptNeural={onAcceptNeuralVoice}
        onUseSystem={onUseSystemVoice}
        onClose={onCloseConsentDialog}
      />
    </>
  );
};
